import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { useTheme, alpha } from "@material-ui/core/styles";
import { red, blue } from "@material-ui/core/colors";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { updateCard } from "../../services/kanban";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import moment from "moment";

const TasksModal = ({ open, onClose, card, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  // Os hex claros fixos não mudam com palette.type: no escuro o modal usa as variantes abaixo
  const isDark = theme.palette.type === "dark";
  const [tasks, setTasks] = useState([]);
  const [newTaskText, setNewTaskText] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Estados para agendamento
  const [isScheduled, setIsScheduled] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactSearchParam, setContactSearchParam] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState(
    moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
  );

  const isConnectedStatus = (status) => String(status || "").toUpperCase() === "CONNECTED";

  const pickDefaultWhatsapp = (list) => {
    if (!Array.isArray(list) || list.length === 0) {
      return null;
    }

    const connected = list.filter((w) => isConnectedStatus(w.status));
    if (connected.length === 0) {
      return null;
    }

    const userDefault = connected.find((w) => Number(w.id) === Number(user?.whatsappId));
    if (userDefault) {
      return userDefault;
    }

    const companyDefault = connected.find((w) => w.isDefault);
    if (companyDefault) {
      return companyDefault;
    }

    const connectedBaileys = connected
      .filter((w) => (w.type || "baileys") !== "cloudapi")
      .sort((a, b) => Number(a.id) - Number(b.id));

    if (connectedBaileys.length > 0) {
      return connectedBaileys[0];
    }

    return connected.sort((a, b) => Number(a.id) - Number(b.id))[0];
  };

  // Carregar tarefas do card
  useEffect(() => {
    if (open && card) {
      setTasks(Array.isArray(card.tasks) ? card.tasks : []);
      setHasChanges(false); // Resetar flag de mudanças ao abrir
      setNewTaskDueDate("");
      // Reset dos estados de agendamento ao abrir modal
      setIsScheduled(false);
      setSelectedContact(null);
      setWhatsapps([]);
      setSelectedWhatsappId("");
      setScheduledDateTime(moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
    }
  }, [open, card]);

  // Buscar contatos conforme o usuário digita
  useEffect(() => {
    if (!isScheduled) return;
    
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          setLoadingContacts(true);
          const { data } = await api.get("/contacts", {
            // searchScope: busca só no cadastro do contato, sem varrer o corpo
            // das mensagens (que traria quem apenas citou a palavra).
            params: {
              searchParam: contactSearchParam,
              pageNumber: 1,
              searchScope: "contact"
            }
          });
          setContacts(data.contacts || []);
        } catch (err) {
          console.error("Erro ao buscar contatos:", err);
        } finally {
          setLoadingContacts(false);
        }
      };
      
      fetchContacts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [contactSearchParam, isScheduled]);

  useEffect(() => {
    if (!isScheduled) {
      return;
    }

    const fetchWhatsapps = async () => {
      try {
        setLoadingWhatsapps(true);
        const { data } = await api.get("/whatsapp", { params: { session: 0 } });
        const connected = Array.isArray(data)
          ? data.filter((w) => isConnectedStatus(w.status))
          : [];

        setWhatsapps(connected);

        const defaultWhatsapp = pickDefaultWhatsapp(connected);
        if (defaultWhatsapp) {
          setSelectedWhatsappId(String(defaultWhatsapp.id));
        } else {
          setSelectedWhatsappId("");
        }
      } catch (err) {
        console.error("Erro ao buscar conexões:", err);
        setWhatsapps([]);
        setSelectedWhatsappId("");
      } finally {
        setLoadingWhatsapps(false);
      }
    };

    fetchWhatsapps();
  }, [isScheduled]);

  // Função para fechar o modal e atualizar dados se houver mudanças
  const handleClose = () => {
    if (hasChanges && onSuccess) {
      console.log('TasksModal: Chamando onSuccess para atualizar o board');
      onSuccess(); // Atualizar dados do board (cards e colunas)
    }
    onClose();
  };

  // Alteracao: GitHub Copilot - 20/04/2026 - suporte a prazo opcional e regra de tarefa vencida por data.
  const normalizeDateOnly = (dateValue) => {
    if (!dateValue) return null;

    if (typeof dateValue === "string") {
      const dateOnlyMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnlyMatch) {
        const year = Number(dateOnlyMatch[1]);
        const month = Number(dateOnlyMatch[2]) - 1;
        const day = Number(dateOnlyMatch[3]);
        const localDate = new Date(year, month, day);

        if (Number.isNaN(localDate.getTime())) return null;
        localDate.setHours(0, 0, 0, 0);
        return localDate;
      }
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return null;

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
  };

  const isTaskOverdue = (task) => {
    if (task.completed || !task.dueDate) return false;

    const dueDate = normalizeDateOnly(task.dueDate);
    if (!dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const formatDueDate = (dateValue) => {
    const parsedDate = normalizeDateOnly(dateValue);
    if (!parsedDate) return null;

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Função para persistir tarefas no banco
  const persistTasks = async (updatedTasks) => {
    if (!card) return;

    try {
      setLoading(true);
      
      // Enviar apenas as tarefas, mantendo outros dados do card
      await updateCard(card.id, {
        tasks: updatedTasks
      });

      console.log('Tarefas persistidas com sucesso:', updatedTasks);
      setHasChanges(true); // Marcar que houve mudanças
    } catch (error) {
      console.error('Erro ao persistir tarefas:', error);
      toast.error('Erro ao salvar tarefas');
    } finally {
      setLoading(false);
    }
  };

  // Adicionar nova tarefa
  const handleAddTask = async () => {
    if (!newTaskText.trim()) {
      toast.warning("Digite o texto da tarefa");
      return;
    }

    // Validar se agendamento está marcado
    if (isScheduled) {
      if (!selectedContact) {
        toast.warning("Selecione um contato para o agendamento");
        return;
      }
      if (!scheduledDateTime) {
        toast.warning("Selecione a data e hora do agendamento");
        return;
      }
      if (!selectedWhatsappId) {
        toast.warning("Selecione a conexão para envio da mensagem");
        return;
      }

      const selectedWhatsapp = whatsapps.find(
        (w) => Number(w.id) === Number(selectedWhatsappId)
      );

      if (!selectedWhatsapp || !isConnectedStatus(selectedWhatsapp.status)) {
        toast.warning("A conexão selecionada não está conectada. Escolha outra conexão.");
        return;
      }
    }

    const newTask = {
      id: Date.now(),
      text: newTaskText.trim(),
      completed: false,
      createdAt: new Date().toISOString(),
      dueDate: newTaskDueDate || null,
    };

    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);

    // Persistir tarefa imediatamente
    await persistTasks(updatedTasks);
    
    // Se agendamento estiver marcado, criar agendamento
    if (isScheduled && selectedContact) {
      try {
        await api.post("/schedules", {
          body: newTaskText.trim(),
          sendAt: scheduledDateTime,
          contactId: selectedContact.id,
          userId: user.id,
          whatsappId: Number(selectedWhatsappId)
        });
        toast.success("Tarefa adicionada e agendamento criado!");
      } catch (error) {
        console.error("Erro ao criar agendamento:", error);
        toast.error("Tarefa adicionada, mas erro ao criar agendamento");
      }
    } else {
      toast.success("Tarefa adicionada!");
    }
    
    // Limpar campos
    setNewTaskText("");
    setNewTaskDueDate("");
    setIsScheduled(false);
    setSelectedContact(null);
    setWhatsapps([]);
    setSelectedWhatsappId("");
    setScheduledDateTime(moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
  };

  // Marcar/desmarcar tarefa como concluída
  const handleToggleTask = async (taskId) => {
    const updatedTasks = tasks.map(task =>
      task.id === taskId
        ? { ...task, completed: !task.completed }
        : task
    );

    setTasks(updatedTasks);

    // Persistir imediatamente
    await persistTasks(updatedTasks);
    
    const task = updatedTasks.find(t => t.id === taskId);
    toast.success(task.completed ? "Tarefa concluída!" : "Tarefa reaberta!");
  };

  // Excluir tarefa
  const handleDeleteTask = async (taskId) => {
    const updatedTasks = tasks.filter(task => task.id !== taskId);
    setTasks(updatedTasks);

    // Persistir imediatamente
    await persistTasks(updatedTasks);
    toast.success("Tarefa excluída!");
  };

  // Alternar exibição de tarefas concluídas
  const handleToggleShowCompleted = () => {
    setShowCompletedTasks(!showCompletedTasks);
  };

  // Obter tarefas visíveis
  const getVisibleTasks = () => {
    if (showCompletedTasks) {
      return tasks;
    }
    return tasks.filter(task => !task.completed);
  };

  // Obter estatísticas
  const getTasksStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(task => task.completed).length;
    const overdue = tasks.filter(task => isTaskOverdue(task)).length;
    return { total, completed, pending: total - completed, overdue };
  };

  const stats = getTasksStats();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Tarefas
            </Typography>
            {tasks.length > 0 && (
              <Chip
                label={`${stats.completed}/${stats.total}`}
                size="small"
                color={stats.completed === stats.total && stats.total > 0 ? "primary" : "default"}
              />
            )}
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Card Info */}
        <Box mb={2} p={2} bgcolor={isDark ? alpha("#fff", 0.05) : "#f5f5f5"} borderRadius={2}>
          <Typography variant="subtitle2" color="textSecondary">
            Card:
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            {card?.customFields?.title || card?.title || "Card sem título"}
          </Typography>
        </Box>

        {/* Filtro de visualização */}
        {tasks.some(t => t.completed) && (
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              size="small"
              startIcon={showCompletedTasks ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={handleToggleShowCompleted}
            >
              {showCompletedTasks ? "Ocultar" : "Mostrar"} Concluídas
            </Button>
          </Box>
        )}

        {/* Input para adicionar nova tarefa */}
        <Box mb={2}>
          {/* Alteracao: GitHub Copilot - 20/04/2026 - adiciona campo opcional de prazo por tarefa. */}
          <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 12 }}>
            {/* Campo de texto da tarefa */}
            <TextField
              size="small"
              variant="outlined"
              placeholder="Digite uma nova tarefa..."
              value={newTaskText}
              onChange={(e) => setNewTaskText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isScheduled) {
                  e.preventDefault();
                  handleAddTask();
                }
              }}
              disabled={loading}
              style={{ flex: 1, minWidth: 220 }}
            />

            <TextField
              type="date"
              size="small"
              variant="outlined"
              label="Prazo (opcional)"
              value={newTaskDueDate}
              onChange={(e) => setNewTaskDueDate(e.target.value)}
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              style={{ width: 190 }}
            />
          </Box>

          {/* Checkbox Agenda */}
          <Box display="flex" alignItems="center" mb={isScheduled ? 2 : 0}>
            <Checkbox
              checked={isScheduled}
              onChange={(e) => setIsScheduled(e.target.checked)}
              color="primary"
              disabled={loading}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<ScheduleIcon style={{ color: '#2196f3' }} />}
              style={{ padding: '4px 8px' }}
            />
            <Typography 
              variant="body2" 
              style={{ 
                fontWeight: isScheduled ? 600 : 400,
                color: isScheduled ? '#2196f3' : 'inherit',
                cursor: 'pointer'
              }}
              onClick={() => !loading && setIsScheduled(!isScheduled)}
            >
              <ScheduleIcon style={{ 
                fontSize: 18, 
                verticalAlign: 'middle', 
                marginRight: 4,
                color: isScheduled ? '#2196f3' : isDark ? theme.palette.text.secondary : '#666'
              }} />
              Agendar envio de mensagem
            </Typography>
          </Box>

          {/* Campos condicionais de agendamento */}
          {isScheduled && (
            <Box 
              p={2.5} 
              bgcolor={isDark ? alpha("#fff", 0.05) : "#f8f9fa"}
              borderRadius={2}
              border={`1px solid ${isDark ? theme.palette.divider : "#e0e0e0"}`}
              mb={2}
            >
              <Typography variant="subtitle2" style={{ marginBottom: 16, fontWeight: 600 }}>
                Detalhes do Agendamento
              </Typography>
              
              <Autocomplete
                size="small"
                options={contacts}
                loading={loadingContacts}
                value={selectedContact}
                onChange={(event, newValue) => setSelectedContact(newValue)}
                onInputChange={(event, newInputValue) => setContactSearchParam(newInputValue)}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  return option.number ? `${option.name} - ${option.number}` : option.name;
                }}
                getOptionSelected={(option, value) => option.id === value.id}
                renderOption={(option) => (
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    {option.number && (
                      <Typography variant="caption" color="textSecondary">
                        {option.number}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contato"
                    variant="outlined"
                    placeholder="Digite o nome ou número..."
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                style={{ marginBottom: 12 }}
              />
              
              <TextField
                type="datetime-local"
                label="Data e Hora do Envio"
                value={scheduledDateTime}
                onChange={(e) => setScheduledDateTime(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                style={{ marginBottom: 12 }}
              />

              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                style={{ marginBottom: 12 }}
              >
                <InputLabel id="tasks-modal-whatsapp-label">Conexão para envio *</InputLabel>
                <Select
                  labelId="tasks-modal-whatsapp-label"
                  label="Conexão para envio *"
                  value={selectedWhatsappId}
                  onChange={(e) => setSelectedWhatsappId(e.target.value)}
                  disabled={loading || loadingWhatsapps}
                >
                  <MenuItem value="">
                    <em>
                      {loadingWhatsapps ? "Carregando conexões..." : "Selecione uma conexão"}
                    </em>
                  </MenuItem>
                  {whatsapps.map((whatsapp) => (
                    <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                      {whatsapp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!loadingWhatsapps && whatsapps.length === 0 && (
                <Typography variant="caption" style={{ color: isDark ? red[300] : '#d32f2f', display: 'block', marginBottom: 12 }}>
                  Nenhuma conexão WhatsApp conectada encontrada. Conecte uma conexão para criar o agendamento.
                </Typography>
              )}
              
              <Box 
                p={1.5} 
                bgcolor={isDark ? alpha(blue[500], 0.15) : "#e3f2fd"}
                borderRadius={1}
                display="flex"
                alignItems="flex-start"
              >
                <ScheduleIcon style={{
                  fontSize: 18,
                  color: isDark ? blue[300] : '#1976d2',
                  marginRight: 8,
                  marginTop: 2
                }} />
                <Typography variant="caption" style={{ color: isDark ? blue[200] : '#1565c0', lineHeight: 1.4 }}>
                  A mensagem com o texto da tarefa será enviada automaticamente para o contato selecionado na data e hora informadas.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Botão adicionar */}
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddTask}
            fullWidth
            disabled={loading}
            style={{ padding: '10px' }}
          >
            {isScheduled ? "Adicionar com Agendamento" : "Adicionar Tarefa"}
          </Button>
        </Box>

        {/* Lista de tarefas */}
        {tasks.length > 0 ? (
          <Box
            border={`1px solid ${isDark ? theme.palette.divider : "#e0e0e0"}`}
            borderRadius="8px"
            maxHeight="400px"
            overflow="auto"
          >
            <List dense>
              {getVisibleTasks().map((task, index, visibleTasks) => {
                const overdue = isTaskOverdue(task);
                const formattedTaskDueDate = formatDueDate(task.dueDate);
                const isLastTask = index === visibleTasks.length - 1;

                return (
                  <ListItem
                    key={task.id}
                    button
                    onClick={() => handleToggleTask(task.id)}
                    disabled={loading}
                    style={{
                      backgroundColor: task.completed
                        ? (isDark ? alpha("#fff", 0.05) : '#f5f5f5')
                        : overdue ? (isDark ? alpha(red[500], 0.15) : '#ffebee') : 'transparent',
                      textDecoration: task.completed ? 'line-through' : 'none',
                      opacity: task.completed ? 0.7 : 1,
                      borderRadius: 6,
                      border: `1px solid ${isDark ? theme.palette.divider : '#eeeeee'}`,
                      marginBottom: isLastTask ? 0 : 8,
                    }}
                  >
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={task.completed}
                        tabIndex={-1}
                        disableRipple
                        disabled={loading}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon style={{ color: '#4caf50' }} />}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={task.text}
                      secondary={formattedTaskDueDate ? `Prazo: ${formattedTaskDueDate}` : null}
                      secondaryTypographyProps={{
                        style: {
                          color: overdue && !task.completed
                            ? (isDark ? red[300] : '#d32f2f')
                            : (isDark ? theme.palette.text.secondary : '#757575'),
                          fontWeight: overdue ? 600 : 400,
                        },
                      }}
                      style={{
                        color: task.completed
                          ? (isDark ? theme.palette.text.secondary : '#757575')
                          : overdue ? (isDark ? red[300] : '#c62828') : 'inherit'
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTask(task.id);
                        }}
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ) : (
          <Box
            py={3}
            textAlign="center"
            color="text.secondary"
            border={`1px dashed ${isDark ? theme.palette.divider : "#e0e0e0"}`}
            borderRadius="8px"
          >
            <Typography variant="body2">
              Nenhuma tarefa adicionada ainda
            </Typography>
          </Box>
        )}

        {/* Estatísticas de tarefas */}
        {tasks.length > 0 && (
          <Box mt={2} display="flex" justifyContent="center" alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="textSecondary" style={{ marginRight: 10 }}>
              Total: {stats.total}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginRight: 10 }}>
              |
            </Typography>
            <Typography variant="caption" style={{ color: '#4caf50', marginRight: 10 }}>
              Concluídas: {stats.completed}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginRight: 10 }}>
              |
            </Typography>
            <Typography variant="caption" style={{ color: '#ff9800', marginRight: stats.overdue > 0 ? 10 : 0 }}>
              Pendentes: {stats.pending}
            </Typography>
            {stats.overdue > 0 && (
              <>
                <Typography variant="caption" color="textSecondary" style={{ marginRight: 10 }}>
                  |
                </Typography>
                <Typography variant="caption" style={{ color: '#f44336', fontWeight: 600 }}>
                Vencidas: {stats.overdue}
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Indicador de salvamento */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
              Salvando...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TasksModal;
